import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Breadcrumbs } from "components/navigation";
import { ShowHide } from "components/layout";
import { FormSelect, FormTextInput, FormLocationSelector } from "components/form-fields";
import { KeyBlock } from "components/key";
import { ListingTable } from "components/listing";
import { AssetStatus } from "components/status/AssetStatus";
import { Button } from "components/buttons";
import { Tooltip } from "components/tooltip";
import { Filter, FilterButton } from "components/filtering";
import { Position } from "@blueprintjs/core";
import { useDebounce } from "hooks/useDebounce";
import { SetFiltering } from "state/actions";
import { EarnedRecognitionService } from "services/EarnedRecognitionService";
import { AssetService } from "services/AssetService";
import { FleetService } from "services/FleetService";
import { UserService } from "services/UserService";
import { LinkService } from "services/LinkService";
import { AssetTypeFilterRenderer } from "pages/vehicle/asset-type-filter-renderer/AssetTypeFilterRenderer";
import classNames from "classnames";
import moment from "moment";
import { isPeriodAtOrBeforeFloor } from "./kpiPeriodWindow";
import "./EarnedRecognition.css";

const defaultArray = [];
const emptyColours = [];

// API/anchor dates are exchanged in ISO date format.
const apiDateFormat = "YYYY-MM-DD";
// Number of ISO weeks in a single ISO period (also the number of week columns in the listing).
const isoWeeksPerPeriod = 4;
// The reporting calendar always has exactly 13 periods per year. In a 53-week ISO year (e.g. 2026)
// the 53rd week folds into period 13 (which then spans five weeks) — period number is capped at 13.
const isoPeriodsPerYear = 13;
// The listing defaults to the first page at 25 rows per page, ordered by Registration ascending.
const defaultPageSize = 25;
const defaultSortDirection = "A";
// Registration is the only sortable column for the maintenance asset listing.
const registrationSortField = "Registration";
// Debounce window for the registration/asset-name search input.
const searchDebounceTimeout = 750;
// Business areas the location selector should offer. Maintenance data is asset-scoped, so the asset
// business areas (Tacho / VIS) are used here, matching the Manage Assets listing.
const locationBusinessAreaIds = [1, 2];
// The M5 (MOT) KPI is derived from the MOT pass/fail rather than a per-week boolean flag.
const motKpiCode = "M5";
// The special-processing key that identifies an asset's PMI-family event types. M1/M2/M3 deep-link
// into the events list pre-filtered to these event types.
const pmiSpecialProcessingKey = "PMI";
// Maps the asset filter panel's front-end names (the AssetTypeFilterRenderer and the status/ownership
// filters label selections with these) to the CheckMS search filter keys the endpoint expects
// (PBI 161105). Subtype selections arrive under "AssetTypeSubtype".
const assetFilterKeyByName = {
    AssetType: "ASSET_TYPE",
    AssetTypeSubtype: "ASSET_SUBTYPE",
    AssetStatus: "ASSET_STATUS",
    AssetOwnership: "ASSET_OWNERSHIP"
};
// Prefix the AssetTypeFilterRenderer uses for an "uncategorised" (no subtype) pseudo selection,
// e.g. "uncat-12" for asset type 12.
const uncategorisedSubtypePrefix = "uncat-";

// Derives the StartDate (first day, Monday) of a four-week ISO period from its YEAR and PERIOD
// NUMBER. Period N begins on ISO week (N - 1) * 4 + 1.
function getIsoPeriodStartFromNumber(isoYear, isoPeriod) {
    const startWeek = (Number(isoPeriod) - 1) * isoWeeksPerPeriod + 1;
    return moment().isoWeekYear(Number(isoYear)).isoWeek(startWeek).startOf("isoWeek").format(apiDateFormat);
}

// Shifts an ISO year/period by whole periods (deltaPeriods may be negative). Works in period space
// (13 periods per ISO year) rather than week arithmetic, so it rolls over year boundaries correctly
// and never produces a period 14 from the 53rd week of a 53-week ISO year.
function shiftIsoPeriod(isoYear, isoPeriod, deltaPeriods) {
    const absolutePeriod = Number(isoYear) * isoPeriodsPerYear + (Number(isoPeriod) - 1) + deltaPeriods;
    return {
        isoYear: Math.floor(absolutePeriod / isoPeriodsPerYear),
        isoPeriod: (absolutePeriod % isoPeriodsPerYear) + 1
    };
}

// The ISO year/period containing today, used to detect (and reset to) the current period. Period is
// capped at 13: week 53 of a 53-week ISO year folds into period 13, not a (non-existent) period 14.
function getCurrentIsoPeriod() {
    return {
        isoYear: moment().isoWeekYear(),
        isoPeriod: Math.min(isoPeriodsPerYear, Math.ceil(moment().isoWeek() / isoWeeksPerPeriod))
    };
}

// Maintenance KPI Drilldown page (PBI 162027). The asset/Check-side sibling of the Driver KPI
// Drilldown. Reached by clicking a KeyBlock on the Maintenance KPI summary; the selected ISO
// year/period and KPI arrive as query-string params. The KPI query param is the single source of
// truth for the selected KPI — the dropdown selection drives navigation, which re-renders the page
// from the new param rather than holding the selection in separate state.
export function MaintenanceKpiDrilldown({ breadcrumbs = defaultArray }) {
    const [searchParams] = useSearchParams();
    const isoYear = searchParams.get("isoYear");
    const isoPeriod = searchParams.get("isoPeriod");
    const kpi = searchParams.get("kpi");
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [kpiOptions, setKpiOptions] = useState(defaultArray);
    const [loading, setLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    // The earliest date the account has KPI data for (when it started sending to DVSA or the calc was
    // turned on), returned by the KPI dashboard endpoint. Bounds how far back the period stepper pages.
    const [kpiStartFloor, setKpiStartFloor] = useState(null);

    // Trigger-threshold bands (per-KPI severity description + pass-rate range) used to name the
    // Period Status severity in the KeyBlock tooltip.
    const [maintenanceThresholds, setMaintenanceThresholds] = useState(defaultArray);

    const [period, setPeriod] = useState(null);
    const [periodLoading, setPeriodLoading] = useState(true);
    const [periodHasError, setPeriodHasError] = useState(false);

    // Asset filter option lists. assetTypes carry nested subtypes (rendered by AssetTypeFilterRenderer);
    // the status/ownership lists also feed the AssetStatus icon's colour/icon mappings in the listing.
    const [assetTypeOptions, setAssetTypeOptions] = useState(defaultArray);
    const [assetStatusOptions, setAssetStatusOptions] = useState(defaultArray);
    const [assetOwnershipOptions, setAssetOwnershipOptions] = useState(defaultArray);
    // Status / ownership attribute mappings used to colour and icon the AssetStatus block per row.
    const [assetStatusMappings, setAssetStatusMappings] = useState(defaultArray);
    const [assetOwnershipMappings, setAssetOwnershipMappings] = useState(defaultArray);

    // Listing state.
    const [assets, setAssets] = useState(defaultArray);
    const [totalRecords, setTotalRecords] = useState(0);
    const [listingLoading, setListingLoading] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [searchTerm, setSearchTerm] = useState("");
    const [tableSortDir, setTableSortDir] = useState(defaultSortDirection);

    // Asset Type / Subtype / Status / Ownership filters sit behind the filter button + panel
    // (mirroring the Manage Assets listing). The panel reports its selections through onFilterChange
    // into appliedFilters.
    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(defaultArray);
    // Expand/collapse state for the nested asset-type subtype menu in the filter panel.
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});

    // Location selector state — the selection is page-scoped, so it is held in local component state.
    const [customLocations, setCustomLocations] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState(defaultArray);
    const [useHierarchy, setUseHierarchy] = useState(true);
    // Tracks whether the asynchronous location load has resolved (success or failure). The listing
    // fetch is gated on this so it fires exactly once, after locations are known — preventing the
    // fresh-mount double-fire whose concurrent second call can clobber the good first response.
    const [locationsLoaded, setLocationsLoaded] = useState(false);

    const debouncedSearchTerm = useDebounce(searchTerm, searchDebounceTimeout);

    // Refs guarding against duplicate fetches when several dependencies settle in the same render.
    const currentPageNumber = useRef();
    const currentPageSize = useRef();
    const currentSearchTerm = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();
    const currentStartDate = useRef();
    const currentKpi = useRef();

    // Monotonic id of the most recently issued listing request. A response is applied only if its id
    // still matches the latest issued id, so a slow earlier response can never overwrite a newer one.
    const requestSequence = useRef(0);

    // The StartDate is derived from the route year/period so the API resolves the same ISO period the
    // user drilled into. It anchors the listing search and the per-row Actions deep links.
    const startDate = getIsoPeriodStartFromNumber(isoYear, isoPeriod);

    // The ISO period containing today. The next/reset stepper controls are disabled when the route is
    // already on this period (no future periods to step to, nothing to reset to).
    const currentPeriod = getCurrentIsoPeriod();
    const isCurrentPeriod = Number(isoYear) === currentPeriod.isoYear && Number(isoPeriod) === currentPeriod.isoPeriod;

    // The earliest period the account has KPI data for. There is nothing to drill into below it, so the
    // Previous stepper is bounded here. A null/absent floor imposes no lower bound.
    const isAtOrBeforeFloor = isPeriodAtOrBeforeFloor(Number(isoYear), Number(isoPeriod), kpiStartFloor);

    // Serialised key of the applied filters (name:value pairs). The effects and the fetch guard key
    // on this so that changing a filter's value (same count, different value) still triggers a
    // refetch.
    const appliedFiltersKey = appliedFilters.map((filter) => `${filter.name}:${filter.value}`).join("|");

    useEffect(() => {
        // The KPI dropdown labels and the Period Status colour come from the Maintenance KPI Dashboard
        // endpoint — the same source the summary page uses. Maintenance KPI keys are already M1-M5, so
        // no key-to-code mapping is needed.
        setLoading(true);
        setHasError(false);
        EarnedRecognitionService.getMaintenanceKpis(startDate).then(
            (response) => {
                setKpiOptions(response.kpis);
                // The floor is account-level and stable, so it is only set on success and never reset to
                // null at fetch start — that keeps the Previous button from flickering enabled while the
                // user steps between periods.
                setKpiStartFloor(response.kpiStartFloor ?? null);
                setLoading(false);
            },
            () => {
                setHasError(true);
                setLoading(false);
            }
        );
    }, [startDate]);

    useEffect(() => {
        // Load the Asset Type / Subtype / Status / Ownership filter options once from the shared asset
        // options endpoint (the same source the asset listing pages use). Failure here is non-fatal:
        // the dropdowns simply offer no values, and the listing still loads.
        AssetService.getAssetOptions(true).then(
            (response) => {
                setAssetTypeOptions(response.assetTypes);
                setAssetStatusOptions(response.assetStatuses);
                setAssetOwnershipOptions(response.assetOwnerships);
                setAssetStatusMappings(response.assetStatuses.map((status) => {
                    return { name: status.name, colours: status.colour, id: status.id };
                }));
                setAssetOwnershipMappings(response.assetOwnerships.map((ownership) => {
                    return { name: ownership.name, colours: ownership.colour, id: ownership.id, className: ownership.style, icon: ownership.icon };
                }));
            },
            () => {
                setAssetTypeOptions(defaultArray);
                setAssetStatusOptions(defaultArray);
                setAssetOwnershipOptions(defaultArray);
                setAssetStatusMappings(defaultArray);
                setAssetOwnershipMappings(defaultArray);
            }
        );
    }, []);

    useEffect(() => {
        // The per-band trigger thresholds (severity description + pass-rate range per KPI) name the
        // Period Status severity in the KeyBlock tooltip. Fetched once from the shared thresholds
        // endpoint; failure is non-fatal — the tooltip is simply not shown.
        EarnedRecognitionService.getKpiThresholds().then(
            (response) => {
                setMaintenanceThresholds(response.maintenanceThresholds ?? defaultArray);
            },
            () => {
                setMaintenanceThresholds(defaultArray);
            }
        );
    }, []);

    useEffect(() => {
        // Load the locations (with hierarchy) for the asset business areas, merged across them, and
        // default the selection to every location — mirroring the Manage Assets listing.
        UserService.getLoggedInUserLocationsForMultipleBusinessAreas(locationBusinessAreaIds, true, true).then(
            (response) => {
                setCustomLocations(response);
                setSelectedLocations(response.location[0].items.map((item) => item.id));
                setLocationsLoaded(true);
            },
            () => {
                setCustomLocations(null);
                setLocationsLoaded(true);
            }
        );
    }, []);

    useEffect(() => {
        // Reset to the first page whenever the period, KPI, search term or any filter changes, so the
        // user is not left on a now-out-of-range page. This runs before the fetch effect below.
        setPageNumber(1);
    }, [startDate, kpi, debouncedSearchTerm, appliedFiltersKey, selectedLocations]);

    useEffect(() => {
        // Wait until the locations have resolved before the first fetch, so the listing fires once with
        // the resolved location filters rather than once before and once after the async load.
        if (!locationsLoaded) {
            return;
        }
        fetchListing();
    }, [isoYear, isoPeriod, kpi, pageNumber, pageSize, tableSortDir, debouncedSearchTerm, appliedFiltersKey, selectedLocations, locationsLoaded]);

    function fetchListing() {
        // Guard against re-running an identical fetch (e.g. when the page-reset effect and a filter
        // change land in the same render). Location selection is compared by its serialised contents.
        const selectedLocationsKey = selectedLocations.join(",");
        if (currentPageNumber.current === pageNumber
            && currentPageSize.current === pageSize
            && currentSearchTerm.current === debouncedSearchTerm
            && currentTableSortDir.current === tableSortDir
            && currentAppliedFilters.current === appliedFiltersKey
            && currentLocations.current === selectedLocationsKey
            && currentStartDate.current === startDate
            && currentKpi.current === kpi) {
            return;
        }

        currentPageNumber.current = pageNumber;
        currentPageSize.current = pageSize;
        currentSearchTerm.current = debouncedSearchTerm;
        currentTableSortDir.current = tableSortDir;
        currentAppliedFilters.current = appliedFiltersKey;
        currentLocations.current = selectedLocationsKey;
        currentStartDate.current = startDate;
        currentKpi.current = kpi;

        // Claim the next request id; only the response carrying this id is allowed to update state.
        const requestId = requestSequence.current + 1;
        requestSequence.current = requestId;

        setListingLoading(true);
        setPeriodLoading(true);
        setPeriodHasError(false);

        // PaginationSearchRequest shape: page size is `requestCount`, and `filters` is a list of
        // { key, value } string pairs. Kpi and StartDate scope the response to the drilled period
        // (the backend derives the ISO period from the Monday of StartDate's week). Each applied
        // asset filter's front-end name is mapped to the endpoint's ASSET_* filter key.
        const filters = [
            { key: "Kpi", value: kpi },
            { key: "StartDate", value: startDate }
        ];
        appliedFilters.forEach((filter) => {
            const filterKey = assetFilterKeyByName[filter.name];
            if (filterKey == null) {
                return;
            }
            // An "uncategorised" subtype selection carries a non-numeric "uncat-{typeId}" value; the
            // subtype filter only accepts integer subtype ids, so collapse it to its parent asset
            // type instead of sending a value that would 400.
            if (filter.name === "AssetTypeSubtype" && typeof filter.value === "string" && filter.value.startsWith(uncategorisedSubtypePrefix)) {
                filters.push({ key: "ASSET_TYPE", value: filter.value.slice(uncategorisedSubtypePrefix.length) });
                return;
            }
            filters.push({ key: filterKey, value: `${filter.value}` });
        });
        selectedLocations.forEach((locationId) => {
            filters.push({ key: "Location", value: `${locationId}` });
        });

        const request = {
            searchTerm: debouncedSearchTerm,
            pageNumber: pageNumber,
            requestCount: pageSize,
            sortField: registrationSortField,
            sortDirection: tableSortDir,
            filters: filters
        };

        EarnedRecognitionService.searchMaintenanceAssets(request).then(
            (response) => {
                // Ignore a response that has been superseded by a later request.
                if (requestId !== requestSequence.current) {
                    return;
                }

                setPeriod(response.period);
                setAssets(response.assets.data);
                setTotalRecords(response.assets.totalCount);
                setPeriodLoading(false);
                setListingLoading(false);
            },
            () => {
                // Ignore a response that has been superseded by a later request.
                if (requestId !== requestSequence.current) {
                    return;
                }

                setPeriodHasError(true);
                setPeriodLoading(false);
                setListingLoading(false);
            }
        );
    }

    // Map the KPI options to the FormSelect item shape ({ id, name }).
    const selectItems = kpiOptions.map((option) => {
        return { id: option.key, name: option.label };
    });

    // Asset Type carries nested subtypes, so it uses the dedicated AssetTypeFilterRenderer; Status and
    // Ownership are standard multi-select filters. The filter `name` is the backend filter key.
    const pageFilters = [
        { displayName: "Asset Type", name: "AssetType", items: assetTypeOptions },
        { displayName: "Asset Status", name: "AssetStatus", items: assetStatusOptions },
        { displayName: "Asset Ownership", name: "AssetOwnership", items: assetOwnershipOptions }
    ];

    // Resolve the heading to the selected KPI's label, falling back to the raw key from the route
    // until the options have loaded.
    const selectedKpi = kpiOptions.find((option) => option.key === kpi);
    const headingText = selectedKpi != null ? selectedKpi.label : kpi;

    // The selected KPI's value for the drilled period (from the dashboard endpoint) supplies the
    // Period Status colour. No match / options not loaded resolves to null and the colour falls back
    // to an uncoloured block.
    const selectedKpiValue = selectedKpi != null && selectedKpi.values != null
        ? selectedKpi.values.find((value) => value.isoYear === Number(isoYear) && value.isoPeriod === Number(isoPeriod))
        : null;

    // The selected KPI's per-period failures / pass percentage come from the search response's
    // period.kpis. `percentage` is a PASS rate (100 - failure rate), so the failure rate shown in the
    // summary is derived as 100 - percentage.
    const selectedPeriodKpi = period != null && period.kpis != null
        ? period.kpis.find((periodKpiEntry) => periodKpiEntry.kpi === kpi)
        : null;

    // Resolves the Period Status KeyBlock colours from the selected KPI's trigger colour for the
    // drilled period. A KPI that did not trigger has no colour, so the block is left empty.
    function resolvePeriodStatusColours() {
        if (period == null || selectedKpiValue == null || selectedKpiValue.triggerColourHex == null) {
            return emptyColours;
        }

        // An open period renders the colour as a bottom-left triangle (KeyBlock draws the fill via its
        // four border colours: top, right, bottom, left); a closed period fills the whole block.
        if (period.isOpen) {
            return ["#FFFFFF", "#FFFFFF", selectedKpiValue.triggerColourHex, selectedKpiValue.triggerColourHex];
        }

        return selectedKpiValue.triggerColourHex;
    }

    // Resolves the Period Status tooltip text: the selected KPI's trigger-threshold band description
    // for the drilled period, with an open-period note appended. The band is matched by KPI code and
    // the period's pass percentage falling within the band's range. Returns an empty string (no
    // tooltip) until the data needed to name a band is available.
    function resolvePeriodStatusTooltip() {
        if (period == null || selectedKpiValue == null || selectedKpiValue.percentage == null) {
            return "";
        }

        const band = maintenanceThresholds.find((threshold) =>
            threshold.kpiCode === kpi
            && selectedKpiValue.percentage >= threshold.minPercentage
            && selectedKpiValue.percentage <= threshold.maxPercentage);

        if (band == null) {
            return "";
        }

        return period.isOpen ? `${band.description} - period open` : band.description;
    }

    // The Period Status value shown beside the KeyBlock: the selected KPI's pass percentage for the
    // drilled period (from the search response's period.kpis), matching the Driver drilldown. Empty
    // until the period summary has loaded.
    function resolvePeriodStatusValue() {
        if (selectedPeriodKpi == null || selectedPeriodKpi.percentage == null) {
            return "";
        }

        return `${selectedPeriodKpi.percentage}%`;
    }

    function handleSelectKpi(selectedItem) {
        navigate(`/earned-recognition/maintenance-kpi?isoYear=${isoYear}&isoPeriod=${isoPeriod}&kpi=${selectedItem.id}`);
    }

    // The ISO period stepper changes the selected period while keeping the current KPI. Each control
    // resolves a target year/period and navigates with the shared maintenance-kpi URL shape.
    function navigateToPeriod(targetYear, targetPeriod) {
        navigate(`/earned-recognition/maintenance-kpi?isoYear=${targetYear}&isoPeriod=${targetPeriod}&kpi=${kpi}`);
    }

    function onPreviousPeriod() {
        if (isAtOrBeforeFloor) {
            return;
        }
        const target = shiftIsoPeriod(isoYear, isoPeriod, -1);
        navigateToPeriod(target.isoYear, target.isoPeriod);
    }

    function onNextPeriod() {
        const target = shiftIsoPeriod(isoYear, isoPeriod, 1);
        navigateToPeriod(target.isoYear, target.isoPeriod);
    }

    function onResetPeriod() {
        navigateToPeriod(currentPeriod.isoYear, currentPeriod.isoPeriod);
    }

    function onSearchChange(event) {
        setSearchTerm(event.target.value);
    }

    function onPagingChange(newPageNumber, newPageSize) {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    }

    function onTableSort(header, direction) {
        setTableSortDir(direction);
    }

    function onToggleFilter() {
        setShowFilters((previous) => {
            return !previous;
        });
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onSelectLocations(locations) {
        setSelectedLocations([...locations]);
    }

    // The selected KPI's pass/fail for a single week. M5 (MOT) is derived from the MOT pass flag on
    // the week the MOT falls in (no MOT that week → no result); every other KPI reads its m{n}Failed
    // boolean. Returns true when the asset FAILED the KPI that week, false when it passed, or null
    // when there is no result to show.
    function getWeekFailed(week) {
        if (kpi === motKpiCode) {
            if (week.motPass == null) {
                return null;
            }
            return !week.motPass;
        }

        switch (kpi) {
            case "M1":
                return week.m1Failed;
            case "M2":
                return week.m2Failed;
            case "M3":
                return week.m3Failed;
            case "M4":
                return week.m4Failed;
            default:
                return null;
        }
    }

    // Builds the tooltip content for a week cell, keyed by the selected KPI. M1/M3 carry no detail
    // (pass/fail only) so they return null and the cell renders without a tooltip. M5 shows the MOT
    // test id and PRS-defect flag; M2 lists each event defect; M4 lists each check defect.
    function buildWeekTooltip(week) {
        if (kpi === motKpiCode) {
            return (
                <div className="er-drilldown-week-tooltip">
                    <div>{`MOT Test ID: ${week.motTestId == null ? "-" : week.motTestId}`}</div>
                    <div>{`MOT PRS Defect: ${formatBoolean(week.motPrsDefect)}`}</div>
                </div>
            );
        }

        if (kpi === "M2") {
            const defects = week.m2Defects || [];
            if (defects.length === 0) {
                return null;
            }

            return (
                <div className="er-drilldown-week-tooltip">
                    {defects.map((defect) => {
                        return (
                            <div key={`m2-${defect.erEventDefectId}`} className="er-drilldown-week-tooltip-defect">
                                <div>{`Defect Type: ${defect.eventDefectTypeId == null ? "-" : defect.eventDefectTypeId}`}</div>
                                <div>{`Comment: ${formatText(defect.defectComment)}`}</div>
                                <div>{`Rectified: ${formatBoolean(defect.rectified)}`}</div>
                                <div>{`Rectified Comment: ${formatText(defect.rectifiedComment)}`}</div>
                                <div>{`Monitored: ${formatBoolean(defect.monitored)}`}</div>
                                <div>{`Maintenance Provider: ${formatText(defect.maintenanceProvider)}`}</div>
                                <div>{`Rectified By: ${formatText(defect.rectifiedBy)}`}</div>
                                <div>{`Rectified Date: ${formatDate(defect.rectifiedDate)}`}</div>
                                <div>{`Signoff User: ${formatText(defect.signoffUser)}`}</div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        if (kpi === "M4") {
            const defects = week.m4Defects || [];
            if (defects.length === 0) {
                return null;
            }

            return (
                <div className="er-drilldown-week-tooltip">
                    {defects.map((defect) => {
                        return (
                            <div key={`m4-${defect.erCheckDefectId}`} className="er-drilldown-week-tooltip-defect">
                                <div>{`Comment: ${formatText(defect.defectComment)}`}</div>
                                <div>{`Rectified: ${formatBoolean(defect.rectified)}`}</div>
                                <div>{`Rectified Comment: ${formatText(defect.rectifiedComment)}`}</div>
                                <div>{`Monitored: ${formatBoolean(defect.monitored)}`}</div>
                                <div>{`Maintenance Provider: ${formatText(defect.maintenanceProvider)}`}</div>
                                <div>{`Rectified By: ${formatText(defect.rectifiedBy)}`}</div>
                                <div>{`Rectified Date: ${formatDate(defect.rectifiedDate)}`}</div>
                                <div>{`Signoff User: ${formatText(defect.signoffUser)}`}</div>
                                <div>{`Defect Status: ${formatText(defect.defectStatus)}`}</div>
                            </div>
                        );
                    })}
                </div>
            );
        }

        // M1 and M3 are pass/fail only — no tooltip.
        return null;
    }

    // Formats a nullable boolean as Yes / No / "-" for tooltip display.
    function formatBoolean(value) {
        if (value == null) {
            return "-";
        }
        return value ? "Yes" : "No";
    }

    // Formats a nullable / empty string as itself or "-" for tooltip display.
    function formatText(value) {
        if (value == null || value === "") {
            return "-";
        }
        return value;
    }

    // Formats a nullable date as DD MMM YYYY or "-" for tooltip display.
    function formatDate(value) {
        if (value == null) {
            return "-";
        }
        return moment(value).format("DD MMM YYYY");
    }

    // Resolves the per-row Actions destination from the selected KPI, pre-applying any filters the
    // destination page reads from Redux, then navigating. The asset profile pages use the "PROFILE"
    // page-state suffix on their filter Redux slices.
    function handleAssetAction(asset) {
        if (kpi === motKpiCode) {
            // M5 → MOT tests: a plain navigate. ?area=ER drives the asset profile's
            // "Earned Recognition" breadcrumb.
            navigate(`/asset/${asset.assetId}/mot-tests?area=ER`);
            return;
        }

        if (kpi === "M4") {
            // M4 → defects. The user may have drilled from an old period, so the asset profile must show
            // the asset's full defect history — an empty date range is seeded so the defects page (which
            // reads its range from Redux) omits the date filter. The KPI-relevant "Check" defect-type
            // filter is still applied. ?area=ER drives the profile's "Earned Recognition" breadcrumb.
            dispatch(SetFiltering("eventHubReduxPROFILE", { startDate: "", endDate: "" }));
            dispatch(SetFiltering("defectListingFilterReduxPROFILE", [{ name: "DEFECTTYPE", value: "Check", displayName: "Check" }]));
            navigate(`/asset/${asset.assetId}/defects?area=ER`);
            return;
        }

        // M1 / M2 / M3 → events list. As with defects, the asset profile shows the asset's full event
        // history (an empty date range is seeded so the events page omits the date filter), while the
        // KPI-relevant PMI event-type filter is still applied. The events page accepts only a single
        // eventTypeId via URL, so the multi-value event-type filter is seeded into Redux instead. Resolve
        // the PMI-family event type ids for this asset before navigating.
        FleetService.getEventTypesForAsset(asset.assetId, true).then(
            (eventTypes) => {
                const pmiTypes = (eventTypes || []).filter((eventType) => {
                    return eventType.specialProcessingKey != null && eventType.specialProcessingKey.toUpperCase() === pmiSpecialProcessingKey;
                });

                dispatch(SetFiltering("eventHubReduxPROFILE", { startDate: "", endDate: "" }));
                dispatch(SetFiltering("eventListingFilterReduxPROFILE", pmiTypes.map((eventType) => {
                    return { name: "EVENTTYPEID", value: `${eventType.eventTypeId}`, displayName: eventType.eventTypeName };
                })));
                navigate(`/asset/${asset.assetId}/events/list?area=ER`);
            },
            () => {
                // If the event types cannot be resolved, still seed the empty range and navigate so the
                // user lands on the events list (unfiltered by event type rather than blocked).
                dispatch(SetFiltering("eventHubReduxPROFILE", { startDate: "", endDate: "" }));
                navigate(`/asset/${asset.assetId}/events/list?area=ER`);
            }
        );
    }

    // Human-readable period range, e.g. "30 March – 26 April 2026".
    let periodRangeText = "";
    if (period != null) {
        periodRangeText = `${moment(period.startDate, apiDateFormat).format("DD MMMM")} – ${moment(period.endDate, apiDateFormat).format("DD MMMM YYYY")}`;
    }

    // The summary tile labels are scoped to the selected KPI, e.g. "M1 Failures".
    const failuresLabel = `${kpi} Failures`;
    const failureRateLabel = `${kpi} Failure Rate (%)`;
    const failuresValue = selectedPeriodKpi != null ? selectedPeriodKpi.failures : "";
    // period.kpis percentage is a PASS rate; the failure rate is its complement.
    const failureRateValue = selectedPeriodKpi != null && selectedPeriodKpi.percentage != null ? `${Math.round((100 - selectedPeriodKpi.percentage) * 100) / 100}%` : "";

    // Week column headers (e.g. "Week 14") derived from the first row's weeks, which carry the week
    // numbers. Falls back to four positional placeholders while the listing is empty/loading so the
    // header count stays stable.
    let weekHeaders = [];
    if (assets.length > 0 && assets[0].weeks != null) {
        weekHeaders = assets[0].weeks.map((week) => `Week ${week.weekNumber}`);
    } else {
        for (let index = 0; index < isoWeeksPerPeriod; index++) {
            weekHeaders.push(`Week ${index + 1}`);
        }
    }

    const headers = [
        "REGISTRATION",
        "LOCATION",
        ...weekHeaders.map((header) => header.toUpperCase()),
        "ACTIONS"
    ];
    // Registration is the only sortable column (per the PBI).
    const sortableHeaders = ["REGISTRATION"];
    const sortedByHeader = "REGISTRATION";

    const rows = assets.map((asset) => {
        // The Asset Type/Status icon shares the Registration column. AssetStatus colours the block by
        // asset status and overlays the ownership icon, with both names as its tooltip.
        const registrationCell = (
            <div className="nowrap inline-items no-spacing">
                <AssetStatus
                    statusId={asset.assetStatus?.id}
                    ownershipId={asset.assetOwnership?.id}
                    isLoading={listingLoading}
                    statusAttributeMappings={assetStatusMappings}
                    ownershipAttributeMappings={assetOwnershipMappings}
                    tooltipPosition={Position.RIGHT}
                />
                <Link to={LinkService.getVehicleProfileUrl(asset.assetId, "details")}>{asset.registration}</Link>
            </div>
        );

        // One cell per ISO week showing the selected KPI's pass/fail. A null result (no data that
        // week) renders as "-"; otherwise a coloured pass/fail indicator. M2/M4/M5 carry a tooltip
        // with the underlying detail; M1/M3 are pass/fail only.
        const weekCells = (asset.weeks || []).map((week) => {
            const failed = getWeekFailed(week);
            const tooltipContent = buildWeekTooltip(week);

            let indicator;
            if (failed == null) {
                indicator = (<span>-</span>);
            } else {
                indicator = (
                    <span className={classNames("er-drilldown-week-result", { "text-intent-danger": failed, "text-intent-success": !failed })}>
                        {failed ? "Fail" : "Pass"}
                    </span>
                );
            }

            if (tooltipContent == null) {
                return (
                    <span key={`week-${asset.assetId}-${week.weekNumber}`}>{indicator}</span>
                );
            }

            return (
                <Tooltip key={`week-${asset.assetId}-${week.weekNumber}`} content={tooltipContent} position="top">
                    {indicator}
                </Tooltip>
            );
        });

        const actionsCell = (
            <div className="button-row-small">
                <Tooltip content="View maintenance detail" position="top">
                    <Button minimal iconOnly large={false} icon="arrow-right" onClick={() => handleAssetAction(asset)} />
                </Tooltip>
            </div>
        );

        return [
            registrationCell,
            asset.locationName,
            ...weekCells,
            actionsCell
        ];
    });

    return (
        <React.Fragment>
            <Breadcrumbs items={breadcrumbs} />
            <h1>{headingText}</h1>
            <div className="inline-items er-drilldown-kpi-row">
                <ShowHide
                    evaluator={hasError}
                    show={(<p>Unable to load KPI options. Please try again later.</p>)}
                    hide={(
                        <FormSelect
                            items={selectItems}
                            selectedValue={kpi}
                            loading={loading}
                            placeholder="Choose a KPI"
                            onItemSelect={handleSelectKpi}
                        />
                    )}
                />
                <div className="er-drilldown-period-stepper">
                    <Tooltip content="Previous period">
                        <Button minimal iconOnly icon="fast-backward" disabled={isAtOrBeforeFloor} onClick={onPreviousPeriod} />
                    </Tooltip>
                    <span className="er-drilldown-period-label">{`Period ${isoPeriod} – ${isoYear}`}</span>
                    <Tooltip content="Next period">
                        <Button minimal iconOnly icon="fast-forward" disabled={isCurrentPeriod} onClick={onNextPeriod} />
                    </Tooltip>
                    <Tooltip content="Current period">
                        <Button minimal iconOnly icon="reset" disabled={isCurrentPeriod} onClick={onResetPeriod} />
                    </Tooltip>
                </div>
            </div>
            <ShowHide
                evaluator={periodHasError}
                show={(<p>Unable to load the period summary. Please try again later.</p>)}
                hide={(
                    <React.Fragment>
                        <div className="er-drilldown-summary">
                            <div className="er-drilldown-summary-period">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Period</span>
                                <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{periodRangeText}</span>
                            </div>
                            <div className="er-drilldown-summary-item">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Period Status</span>
                                <div className="er-drilldown-summary-status">
                                    <KeyBlock colours={resolvePeriodStatusColours()} toolTipText={resolvePeriodStatusTooltip()} loading={periodLoading} />
                                    <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{resolvePeriodStatusValue()}</span>
                                </div>
                            </div>
                            <div className="er-drilldown-summary-item">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>Asset Count</span>
                                <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{period != null ? period.assetCount : ""}</span>
                            </div>
                            <div className="er-drilldown-summary-item">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>{failuresLabel}</span>
                                <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{failuresValue}</span>
                            </div>
                            <div className="er-drilldown-summary-item">
                                <span className={classNames("er-drilldown-summary-label", { "bp6-skeleton": periodLoading })}>{failureRateLabel}</span>
                                <span className={classNames("er-drilldown-summary-value", { "bp6-skeleton": periodLoading })}>{failureRateValue}</span>
                            </div>
                        </div>
                        <div className="spacer-bottom"></div>
                        <div className="filter-controls">
                            <div className="inline-items">
                                <div className="inline-item fill">
                                    <div className="form-field-full">
                                        <FormTextInput
                                            id="er-maintenance-drilldown-asset-search"
                                            placeholder="Registration or Asset Name"
                                            value={searchTerm}
                                            onChange={onSearchChange}
                                            disabled={listingLoading}
                                            large
                                            icon="search"
                                        />
                                    </div>
                                </div>
                                <div className="inline-item">
                                    <FormLocationSelector
                                        businessArea=""
                                        loading={customLocations == null}
                                        disabled={listingLoading}
                                        selectedLocations={selectedLocations}
                                        setSelectedLocations={onSelectLocations}
                                        useHierarchy={useHierarchy}
                                        setUseHierarchy={setUseHierarchy}
                                        useCustomLocations
                                        customLocations={customLocations}
                                        useLocationDefaults={false}
                                        locationDefaultsToTrue
                                    />
                                </div>
                                <div className="inline-item">
                                    <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                </div>
                            </div>
                            <Filter
                                filterName="maintenanceKpiDrilldown"
                                visible={showFilters}
                                filters={pageFilters}
                                onUpdate={onFilterChange}
                                onClickOutside={onToggleFilter}
                                customRenderers={{
                                    AssetType: (props) => (
                                        <AssetTypeFilterRenderer
                                            {...props}
                                            expandedAssetTypes={expandedAssetTypes}
                                            setExpandedAssetTypes={setExpandedAssetTypes}
                                        />
                                    )
                                }}
                            />
                        </div>
                        <div className="spacer-bottom"></div>
                        <ListingTable
                            id="er-maintenance-drilldown-listing"
                            headers={headers}
                            data={rows}
                            totalRecordCount={totalRecords}
                            onPagingChange={onPagingChange}
                            loadingData={listingLoading}
                            noDataMessage="No assets were found for your search criteria"
                            sortable
                            sortableHeaders={sortableHeaders}
                            sortedBy={sortedByHeader}
                            sortedDir={tableSortDir}
                            onSort={onTableSort}
                        />
                    </React.Fragment>
                )}
            />
        </React.Fragment>
    );
}

MaintenanceKpiDrilldown.propTypes = {
    breadcrumbs: PropTypes.array
};
